'use client';

import TestTubeIconSvg from '@/assets/svgs/test-tube-filled.svg';
import { TestContactList } from '@/containers/ContactLists/types';
import { useSendTestMessage } from '@/containers/Jobs/hooks/useJobTests';
import { useToast } from '@/containers/ToastMessage';
import classNames from 'classnames';
import { useSearchParams } from 'next/navigation';
import { useRef, useState } from 'react';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { Modals } from '../all-modals';
import { JobTestModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import ContactSelection from './components/ContactSelection';
import TestListSelector from './components/TestListSelector';

interface JobTestModalProps extends ModalProps {
  params?: JobTestModalParams;
}

const modalName = Modals.JobTestModal;
const JobTestModal = ({
  params: paramsFromProps,
  variant = 'default',
  className,
}: JobTestModalProps) => {
  // Get search params directly from URL as a more reliable source
  const searchParams = useSearchParams();

  const getSanitizedParam = (paramName: string): string | undefined => {
    const value = searchParams.get(paramName);
    // Treat string "null" or "undefined" as not present
    if (value && value !== 'null' && value !== 'undefined') {
      return value;
    }
    return undefined;
  };

  // Prioritize URL search params (sanitized), fallback to props if necessary
  const jobIdFromUrl = getSanitizedParam('jobId');
  const identityIdFromUrl = getSanitizedParam('identityId');
  const accountIdFromUrl = getSanitizedParam('accountId');
  const createdTestIdFromUrl = getSanitizedParam('createdTestId');

  // Use derived values, preferring URL source
  const jobId = jobIdFromUrl || paramsFromProps?.jobId;
  const identityId = identityIdFromUrl || paramsFromProps?.identityId;
  const accountId = accountIdFromUrl || paramsFromProps?.accountId;
  const createdTestId = createdTestIdFromUrl || paramsFromProps?.createdTestId;

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const { isModalOpen, onClose } = useModals({ modalName });
  const { showError } = useToast();

  const [selectedTestList, setSelectedTestList] = useState<TestContactList | null>(null);
  const [contactsSent, setContactsSent] = useState<Record<string, boolean>>({});
  const [sendingMessageTo, setSendingMessageTo] = useState<Record<string, boolean>>({});

  // Check if required params for *sending messages* are missing
  const hasMissingSendParams = !jobId || !identityId || !accountId || !createdTestId;

  const { mutate: sendTestMessage, isPending: isSendingMessage } = useSendTestMessage();

  const handleSendMessage = (phone: string) => {
    if (!selectedTestList || !createdTestId) return;

    setSendingMessageTo((prev) => ({ ...prev, [phone]: true }));

    sendTestMessage(
      {
        testJobId: createdTestId, // Use createdTestId directly
        testContactPhone: phone,
        testListId: selectedTestList.list_id.toString(),
      },
      {
        onSuccess: () => {
          setContactsSent((prev) => ({ ...prev, [phone]: true }));
          setSendingMessageTo((prev) => {
            const updated = { ...prev };
            delete updated[phone];
            return updated;
          });
        },
        onError: (error) => {
          showError(`Failed to send test message: ${error.message}`);
          setSendingMessageTo((prev) => {
            const updated = { ...prev };
            delete updated[phone];
            return updated;
          });
        },
      }
    );
  };

  let modalContent;
  if (hasMissingSendParams) {
    // This check should come before attempting to use these params
    modalContent = (
      <div className="text-error flex flex-col items-center justify-center gap-4 p-8">
        <h3 className="text-xl font-semibold">Missing Parameters</h3>
        <p>
          Job ID, Identity ID, Account ID, and Test ID are required to send test messages.
        </p>
        <button className="btn btn-primary mt-4" onClick={onClose}>
          Close
        </button>
      </div>
    );
  } else {
    // If we have createdTestId and other necessary params, render the main content
    modalContent = (
      <div className="flex flex-col gap-6">
        <h2 className="text-xl font-semibold">Send Test Messages</h2>
        <TestListSelector
          identityId={identityId as string}
          accountId={accountId as string}
          onSelect={setSelectedTestList}
          selected={selectedTestList}
        />

        {selectedTestList && (
          <ContactSelection
            testList={selectedTestList}
            contactsSent={contactsSent}
            onSendMessage={handleSendMessage}
            isSendingMessage={isSendingMessage}
            sendingMessageTo={sendingMessageTo}
          />
        )}

        <div className="mt-4 flex justify-end">
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={true}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <div className="flex h-full flex-col">
          <ModalHeader
            modalName={modalName}
            icon={<TestTubeIconSvg className="h-5 w-5" />}
            title="Test Job"
          />
          <ModalBody ref={modalBodyRef} className="px-5">
            {modalContent}
          </ModalBody>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default JobTestModal;
